
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import HeroSlider from './components/HeroSlider';
import ServicesPreview from './components/ServicesPreview';
import apiClient from '../../lib/api';
import { resolveImageUrl } from '../../lib/utils';

interface Offer {
  id: string;
  title: string;
  description: string;
  discount_text?: string;
  image_url?: string;
  valid_from: string;
  valid_until: string;
  is_active: number;
}

interface Testimonial {
  id: string;
  name: string;
  location?: string;
  rating: number;
  comment: string;
  image_url?: string;
  is_featured?: number;
  is_active: number;
}

const HomePage = () => {
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      const [settings, offersData, testimonialsData] = await Promise.all([
        apiClient.getHomeSettings(),
        apiClient.getOffers(),
        apiClient.getTestimonials()
      ]);

      setHomeSettings(settings);
      setOffers(offersData.filter((offer: Offer) => offer.is_active));
      setTestimonials(testimonialsData.filter((testimonial: Testimonial) => testimonial.is_active));
    } catch (error) {
      console.error('Failed to load home data:', error);
      // Set empty arrays if API fails - admin should add content through admin panel
      setOffers([]);
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  const whatsappBooking = () => {
    const message = "Hi! I would like to book an appointment at Fully A/C Salon. Please let me know your availability.";
    const phoneNumber = homeSettings?.whatsapp_number || '917484984048';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSlider settings={homeSettings} />

      {/* Services Preview */}
      <ServicesPreview />

      {/* Special Offers Section */}
      <section className="py-20 luxury-gradient">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Special Offers
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 text-shadow-luxury">
              Exclusive Deals
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Take advantage of our limited-time offers and experience premium grooming at unbeatable prices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className={`glass-effect rounded-2xl p-6 hover:scale-105 transition-all duration-500 animate-fadeInUp h-full`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={resolveImageUrl(offer.image_url) || 'https://readdy.ai/api/search-image?query=luxury%20salon%20offer%20placeholder&width=400&height=250&seq=default&orientation=landscape'}
                      alt={offer.title}
                      className="w-40 h-28 object-cover rounded-lg"
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start space-x-3 mb-2">
                      <h4 className="text-xl font-bold text-white">{offer.title}</h4>
                      <span className="bg-primary-400 text-black px-3 py-1 rounded-full text-sm font-bold">
                        {offer.discount_text || 'Special Offer'}
                      </span>
                    </div>
                    <p className="text-gray-300 mb-2">{offer.description}</p>
                    <p className="text-sm text-primary-400">Valid until: {new Date(offer.valid_until).toLocaleDateString()}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={whatsappBooking}
                      className="bg-primary-400 hover:bg-primary-500 text-black px-6 py-2 rounded-full font-semibold transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer"
                    >
                      Claim Offer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-primary-400 text-lg font-semibold mb-4 tracking-wide uppercase">
              Testimonials
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Don't just take our word for it. Here's what our satisfied clients have to say about their experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`bg-gray-50 rounded-2xl p-6 shadow-lg hover:shadow-glow transition-all duration-500 animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src={resolveImageUrl(testimonial.image_url) || `https://readdy.ai/api/search-image?query=professional%20customer%20portrait&width=100&height=100&seq=default${testimonial.id}&orientation=squarish`}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    {testimonial.location && (
                      <p className="text-gray-600 text-sm">{testimonial.location}</p>
                    )}
                    <div className="flex space-x-1 mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-primary-400 text-sm"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">
                  "{testimonial.comment}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gold-gradient">
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Ready to Transform Your Look?
            </h2>
            <p className="text-xl text-gray-800 mb-8 leading-relaxed">
              Book your appointment today and experience the luxury of professional grooming
              in our fully air-conditioned salon. Premium service guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 btn-hover-effect whitespace-nowrap cursor-pointer inline-block"
              >
                Book Appointment
              </Link>
              <button
                onClick={whatsappBooking}
                className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                WhatsApp Us
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
